import React, { FC } from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChartOutlined } from '@mui/icons-material';
import { formatNumberMetric } from '../../utils/commonUtils';

interface IViolationDataItem {
	type: string;
	count: number;
	percentage: number;
}

interface ICustomTooltipProps {
	active?: boolean;
	payload?: Array<{
		payload: IViolationDataItem;
	}>;
}

const CustomTooltip: FC<ICustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          p: 1.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
          {data.type}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Count: <strong>{formatNumberMetric(data.count)}</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Percentage: <strong>{formatNumberMetric(data.percentage, 2)}%</strong>
        </Typography>
      </Box>
    );
  }
  return null;
};

interface ICustomTickProps {
	x?: number;
	y?: number;
	payload?: {
		value: string;
	};
}

// Custom tick component for multi-line labels
const CustomTick: FC<ICustomTickProps> = ({ x, y, payload }) => {
  if (!payload?.value) return null;
  
  const words = payload.value.split(' ');
  const shouldWrap = words.length >= 3;
  
  if (shouldWrap) {
    const mid = Math.ceil(words.length / 2);
    const line1 = words.slice(0, mid).join(' ');
    const line2 = words.slice(mid).join(' ');
    
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#666" fontSize={10} transform="rotate(-45)">
          <tspan x="0" dy="0">{line1}</tspan>
          <tspan x="0" dy="12">{line2}</tspan>
        </text>
      </g>
    );
  }
  
  // For 2-word labels, also split them for consistent alignment
  if (words.length === 2) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} textAnchor="end" fill="#666" fontSize={10} transform="rotate(-45)">
          <tspan x="0" dy="0">{words[0]}</tspan>
          <tspan x="0" dy="12">{words[1]}</tspan>
        </text>
      </g>
    );
  }
  
  // Single word labels
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="end" fill="#666" fontSize={10} transform="rotate(-45)">
        {payload.value}
      </text>
    </g>
  );
};

interface IViolationBarChartProps {
	data: IViolationDataItem[];
	showLabels?: boolean;
	chartRef?: React.RefObject<any>;
}

const ViolationBarChart: FC<IViolationBarChartProps> = ({ data, showLabels = false, chartRef }) => {
  const hasData = Array.isArray(data) && data.length > 0;
  const displayData = hasData ? data.slice(0, 8) : [];

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
      }}
    >
      <CardContent sx={{ p: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary', mb: 0.5 }}>
            Top Violation Types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Number of violations by type
          </Typography>
        </Box>
        {hasData ? (
          <Box ref={chartRef}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={displayData} margin={{ bottom: 30, top: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="type" 
                  height={60}
                  tick={<CustomTick />}
                  interval={0}
                />
                <YAxis tickFormatter={(value: number) => formatNumberMetric(value)} />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="count" 
                  fill="#dc2626" 
                  radius={[8, 8, 0, 0]}
                  label={showLabels ? { position: 'top', fill: '#000', fontSize: 11, fontWeight: 'bold', formatter: (value: any) => formatNumberMetric(value) } : false}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 400,
              py: 4,
            }}
          >
            <BarChartOutlined sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
              No Data Available
            </Typography>
            <Typography variant="body2" color="text.disabled">
              No violation data found for the selected filters
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ViolationBarChart;
